import { Controller } from "@hotwired/stimulus";
import tippy from 'tippy.js';
import flash from '../lib/flashes';

export default class extends Controller {
  static targets = [
    "rulesContainer",
    "rulesDisplay",
    "ruleTemplate",
    "hiddenFieldsTemplate",
    "configurationTypeSelect",
    "assetLayoutSelect",
    "existingLayoutSelect",
    "newLayoutInput"
  ];

  static values = {
    rules: Array,
    defaultConfigType: String,
    defaultAssetLayout: String,
    fieldName: String
  };

  connect() {
    this.ruleCounter = this.rulesValue.length;
    this.rulesValue.forEach((ruleData, index) => {
      this.createRuleFromTemplate(index, ruleData);
    });
    // Filter dropdowns after all rules are created
    this.updateConfigTypeDropdowns();
  }

  addRule(event) {
    event.preventDefault();

    // Get default values from Stimulus data attributes
    const defaultAssetLayoutId = this.defaultAssetLayoutValue;

    // Find the first unused configuration type
    const firstUnusedConfigType = this.getFirstUnusedConfigType();
    
    if (!firstUnusedConfigType) {
      alert("All configuration types are already in use");
      return;
    }

    if (!defaultAssetLayoutId) {
      alert("No asset layouts available");
      return;
    }

    const newIndex = this.ruleCounter++;

    // Create rule data once and reuse
    const ruleData = {
      id: null,
      configuration_type_id: firstUnusedConfigType,
      asset_layout_id: defaultAssetLayoutId,
      new_asset_layout_name: "",
      _destroy: false
    };

    this.createHiddenFields(newIndex, ruleData);
    this.createRuleFromTemplate(newIndex, ruleData);
    // Update dropdowns to filter out used types
    this.updateConfigTypeDropdowns();
    tippy('[data-tippy-content]');
  }

  removeRule(event) {
    event.preventDefault();

    if (!confirm("Are you sure?")) {
      return;
    }

    // Get the rule index from the button. If the `i` element was clicked, navigate to the parent, which should be the button element.
    const ruleIndex = event.target.dataset.ruleIndex || event.target.parentElement.dataset.ruleIndex;

    // Use helper method to find hidden field
    const destroyField = this.findHiddenField(ruleIndex, "_destroy");
    if (destroyField) {
      destroyField.value = "true";
    }

    // Use helper method to find rule section
    const ruleSection = this.findRuleSection(ruleIndex);
    if (ruleSection) {
      ruleSection.querySelectorAll('[data-tippy-content]').forEach(el => el._tippy?.destroy());
      ruleSection.style.display = "none";
      ruleSection.dataset.ruleDestroyed = "true";
    }
    // Update dropdowns to show the removed type in other rules
    this.updateConfigTypeDropdowns();

    flash("Sort rule deleted", "success");
  }

  toggleNewName(event) {
    event.preventDefault();

    const ruleIndex = event.target.dataset.ruleIndex;
    const existingLayoutDiv = this.findRuleTarget("existingLayoutSelect", ruleIndex);
    const newLayoutDiv = this.findRuleTarget("newLayoutInput", ruleIndex);

    existingLayoutDiv.style.display = "none";
    newLayoutDiv.style.display = "block";
  }

  removeNewName(event) {
    event.preventDefault();

    const ruleIndex = event.target.dataset.ruleIndex;
    const existingLayoutDiv = this.findRuleTarget("existingLayoutSelect", ruleIndex);
    const newLayoutDiv = this.findRuleTarget("newLayoutInput", ruleIndex);
    const newNameInput = newLayoutDiv.querySelector('input');

    existingLayoutDiv.style.display = "block";
    newLayoutDiv.style.display = "none";

    newNameInput.value = "";
    this.updateHiddenField(ruleIndex, "new_asset_layout_name", "");

    // Set to first available asset layout
    const select = existingLayoutDiv.querySelector('select');
    if (select && select.options.length > 0) {
      const firstOption = select.options[0];
      select.value = firstOption.value;
      this.updateHiddenField(ruleIndex, "asset_layout_id", firstOption.value);
    }
  }

  updateConfigurationType(event) {
    const ruleIndex = event.target.dataset.ruleIndex;
    const value = event.target.value;
    this.updateHiddenField(ruleIndex, "configuration_type_id", value);
    // Update dropdowns to hide newly selected type from other rules
    this.updateConfigTypeDropdowns();
  }

  updateAssetLayout(event) {
    const ruleIndex = event.target.dataset.ruleIndex;
    const value = event.target.value;
    this.updateHiddenField(ruleIndex, "asset_layout_id", value);
  }

  updateNewAssetLayoutName(event) {
    const ruleIndex = event.target.dataset.ruleIndex;
    const value = event.target.value;
    this.updateHiddenField(ruleIndex, "new_asset_layout_name", value);
  }

  createHiddenFields(index, ruleData) {
    const template = this.hiddenFieldsTemplateTarget;
    const clone = template.content.cloneNode(true);

    const container = clone.querySelector('div');
    this.updateTemplateIndexes(container, index);

    // Set values for the hidden fields
    const fields = ['id', 'configuration_type_id', 'asset_layout_id', '_destroy', 'new_asset_layout_name'];
    fields.forEach(field => {
      const input = container.querySelector(`input[name*="[${field}]"]`);
      if (input) {
        input.value = ruleData[field] || '';
      }
    });

    this.rulesContainerTarget.appendChild(container);
  }

  createRuleFromTemplate(index, ruleData) {
    const template = this.ruleTemplateTarget;
    const clone = template.content.cloneNode(true);

    const section = clone.querySelector('section');
    this.updateTemplateIndexes(section, index);

    // Add to DOM first so we can use targets
    this.rulesDisplayTarget.appendChild(section);

    // Set selected values for dropdowns
    const configSelect = this.findRuleTarget("configurationTypeSelect", index);
    const assetLayoutSelect = this.findRuleTarget("assetLayoutSelect", index);

    if (configSelect && ruleData.configuration_type_id) {
      configSelect.value = ruleData.configuration_type_id;
    }
    if (assetLayoutSelect && ruleData.asset_layout_id) {
      assetLayoutSelect.value = ruleData.asset_layout_id;
    }

    if (ruleData.new_asset_layout_name) {
      const existingDiv = this.findRuleTarget("existingLayoutSelect", index);
      const newDiv = this.findRuleTarget("newLayoutInput", index);
      const input = newDiv.querySelector('input');

      existingDiv.style.display = "none";
      newDiv.style.display = "block";
      input.value = ruleData.new_asset_layout_name;
    }
  }

  // Helper method to find rule targets by index (reduces repetition)
  findRuleTarget(targetName, ruleIndex) {
    const targets = this[`${targetName}Targets`];
    return targets.find(target => target.dataset.ruleIndex === ruleIndex.toString());
  }

  // Helper method to find rule sections by index
  findRuleSection(ruleIndex) {
    return this.rulesDisplayTarget.querySelector(`[data-rule-index="${ruleIndex}"]`);
  }

  // Helper method to find hidden fields by index and field name
  findHiddenField(ruleIndex, fieldName) {
    return this.rulesContainerTarget.querySelector(
      `input[name="integrator[${this.fieldNameValue}][${ruleIndex}][${fieldName}]"]`
    );
  }

  updateTemplateIndexes(element, index) {
    // Update data-rule-index attributes
    const elementsWithIndex = element.querySelectorAll('[data-rule-index="TEMPLATE_INDEX"]');
    elementsWithIndex.forEach(el => {
      el.dataset.ruleIndex = index;
    });
    if (element.dataset && element.dataset.ruleIndex === "TEMPLATE_INDEX") {
      element.dataset.ruleIndex = index;
    }

    // Update hidden field name attributes
    const hiddenInputs = element.querySelectorAll('input[name*="TEMPLATE_INDEX"]');
    hiddenInputs.forEach(input => {
      input.name = input.name.replace(/TEMPLATE_INDEX/g, index);
    });
  }

  updateHiddenField(ruleIndex, fieldName, value) {
    const field = this.findHiddenField(ruleIndex, fieldName);
    if (field) {
      field.value = value;
    }
  }

  // Get all configuration type IDs currently in use by active (non-destroyed) rules
  getUsedConfigTypeIds() {
    const usedIds = new Map(); // Map of configTypeId -> ruleIndex
    
    this.configurationTypeSelectTargets.forEach(select => {
      const ruleIndex = select.dataset.ruleIndex;
      const ruleSection = this.findRuleSection(ruleIndex);
      
      // Only count if the rule is not destroyed
      if (ruleSection && ruleSection.dataset.ruleDestroyed !== "true") {
        usedIds.set(select.value.trim(), ruleIndex);
      }
    });
    
    return usedIds;
  }

  // Get the first configuration type ID that is not already used by any rule
  getFirstUnusedConfigType() {
    const usedIds = this.getUsedConfigTypeIds();
    
    // Get all available config types from the template or an existing dropdown
    const templateSelect = this.ruleTemplateTarget.content.querySelector('[data-layout-sorter-target="configurationTypeSelect"]');
    if (!templateSelect) {
      return this.defaultConfigTypeValue; // Fallback
    }
    
    // Find the first option that's not used
    for (const option of templateSelect.options) {
      const normalizedValue = option.value.trim();
      if (!usedIds.has(normalizedValue)) {
        return normalizedValue;
      }
    }
    
    // All types are used, return null
    return null;
  }

  // Update all configuration type dropdowns to hide options used by other rules
  updateConfigTypeDropdowns() {
    const usedIds = this.getUsedConfigTypeIds();
    
    // Filter to only active (non-destroyed) rule selects
    const activeSelects = this.configurationTypeSelectTargets.filter(select => {
      const ruleSection = this.findRuleSection(select.dataset.ruleIndex);
      return ruleSection && ruleSection.dataset.ruleDestroyed !== "true";
    });
    
    activeSelects.forEach(select => {
      const currentRuleIndex = select.dataset.ruleIndex;
      
      // For each option, hide it if it's used by another rule
      Array.from(select.options).forEach(option => {
        const optionValue = option.value.trim();
        const usedByRuleIndex = usedIds.get(optionValue);
        
        if (usedByRuleIndex !== undefined && usedByRuleIndex !== currentRuleIndex) {
          // Used by another rule - hide it
          option.hidden = true;
          option.disabled = true;
        } else {
          // Not used or used by current rule - show it
          option.hidden = false;
          option.disabled = false;
        }
      });
    });
  }
} 